import { useEffect, useMemo, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import CloseIcon from '@mui/icons-material/Close';
import {
  ErrorHistoryEntry,
  formatErrorCodeMessage,
  formatErrorOccurredAt,
} from './errorHistoryUtils';
import './outOfOrderModal.scss';

export type OutOfOrderModalTarget = {
  macId: string;
  name: string;
  /** true = mark OOO, false = clear OOO */
  enabled: boolean;
};

type Props = {
  open: boolean;
  target: OutOfOrderModalTarget | null;
  location: string;
  busy?: boolean;
  error?: string | null;
  onClose: () => void;
  onSubmit: (payload: { reason?: string }) => void;
  on: (event: string, callback: (data: unknown) => void) => (() => void) | void;
  emit: (event: string, payload?: unknown) => void;
  isConnected?: boolean;
};

function isValidHttpUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

const OutOfOrderModal = ({
  open,
  target,
  location,
  busy,
  error,
  onClose,
  onSubmit,
  on,
  emit,
  isConnected,
}: Props) => {
  const [reason, setReason] = useState('');
  const [cmmsUrl, setCmmsUrl] = useState('');
  const [cmmsTouched, setCmmsTouched] = useState(false);
  const [errorHistory, setErrorHistory] = useState<ErrorHistoryEntry[]>([]);
  const [errorHistoryLoading, setErrorHistoryLoading] = useState(true);

  const enabled = target?.enabled ?? true;
  const macId = target?.macId || '';
  const machineName = target?.name || '';

  const cmmsTrimmed = cmmsUrl.trim();
  const cmmsInvalid = cmmsTrimmed !== '' && !isValidHttpUrl(cmmsTrimmed);
  const reasonTrimmed = reason.trim();
  const canSubmit =
    !busy &&
    !!macId &&
    (enabled ? reasonTrimmed.length > 0 && !cmmsInvalid : true);

  useEffect(() => {
    if (!open) return;
    setReason('');
    setCmmsUrl('');
    setCmmsTouched(false);
    setErrorHistory([]);
    setErrorHistoryLoading(true);

    return () => {
      if (isConnected) {
        emit('monitor-history-stop');
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, macId]);

  useEffect(() => {
    if (!open || !enabled || !macId || !location || !isConnected) return;

    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 7);
    emit('monitor-history-client', {
      startDate: startDate.toISOString().split('T')[0],
      endDate: new Date().toISOString().split('T')[0],
      siteId: location,
      Machine: macId,
      limit: 10,
    });
  }, [open, enabled, location, macId, emit, isConnected]);

  useEffect(() => {
    if (!open || !enabled || !on) return;

    const handleMonitorError = () => {
      setErrorHistoryLoading(false);
    };

    const handleMachineErrorHistory = (data: unknown) => {
      setErrorHistoryLoading(false);
      setErrorHistory(Array.isArray(data) ? (data as ErrorHistoryEntry[]) : []);
    };

    const cleanupMonitorError = on('monitor-error', handleMonitorError);
    const cleanupMachineErrorHistory = on('error-logs', handleMachineErrorHistory);

    return () => {
      try {
        if (typeof cleanupMonitorError === 'function') cleanupMonitorError();
        if (typeof cleanupMachineErrorHistory === 'function') cleanupMachineErrorHistory();
      } catch {
        // Ignore cleanup errors
      }
    };
  }, [open, enabled, on]);

  const title = useMemo(() => {
    const label = machineName || macId;
    return enabled ? `Mark out of order — ${label}` : `Clear out of order — ${label}`;
  }, [enabled, machineName, macId]);

  const handleSubmit = () => {
    if (!canSubmit) return;
    if (!enabled) {
      onSubmit({});
      return;
    }
    const composed =
      cmmsTrimmed !== ''
        ? `${reasonTrimmed}\n\nCMMS: ${cmmsTrimmed}`
        : reasonTrimmed;
    onSubmit({ reason: composed });
  };

  const handleClose = () => {
    if (busy) return;
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth={enabled ? 'sm' : 'xs'}
    >
      <DialogTitle>
        <span>{title}</span>
        <IconButton aria-label="close" onClick={handleClose} size="small" disabled={busy}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {enabled ? (
          <>
            <TextField
              label="Reason"
              required
              multiline
              minRows={3}
              fullWidth
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={busy}
              placeholder="Why is this machine out of order?"
              sx={{ mb: 2 }}
            />
            <TextField
              label="CMMS link (optional)"
              fullWidth
              value={cmmsUrl}
              onChange={(e) => setCmmsUrl(e.target.value)}
              onBlur={() => setCmmsTouched(true)}
              disabled={busy}
              placeholder="https://…"
              error={cmmsTouched && cmmsInvalid}
              helperText={
                cmmsTouched && cmmsInvalid
                  ? 'Enter a valid http(s) URL'
                  : 'Paste a Nitro work-order URL if you have one'
              }
              sx={{ mb: 2 }}
            />

            <section className="out-of-order-errors">
              <h3>Recent errors (last 10)</h3>
              {!isConnected ? (
                <div className="out-of-order-empty">Waiting for live connection…</div>
              ) : errorHistoryLoading ? (
                <div className="out-of-order-empty">Loading error history…</div>
              ) : errorHistory.length === 0 ? (
                <div className="out-of-order-empty">No error history recorded.</div>
              ) : (
                <div className="out-of-order-errors-table">
                  <div className="out-of-order-errors-row out-of-order-errors-head">
                    <div>Timestamp</div>
                    <div className="align-right">Error</div>
                  </div>
                  {errorHistory.slice(0, 10).map((entry, index) => (
                    <div
                      key={`ooo-error-${entry.id || index}`}
                      className="out-of-order-errors-row"
                    >
                      <div>{formatErrorOccurredAt(entry)}</div>
                      <div className="align-right">{formatErrorCodeMessage(entry)}</div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </>
        ) : (
          <p className="out-of-order-clear-copy">
            Clear the out-of-order override for{' '}
            <strong>{machineName || macId}</strong>? The machine will return to its
            Alliance-reported status.
          </p>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color={enabled ? 'warning' : 'primary'}
          onClick={handleSubmit}
          disabled={!canSubmit}
        >
          {busy ? 'Saving…' : enabled ? 'Mark out of order' : 'Clear out of order'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OutOfOrderModal;
